import { useRef } from "react";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/view/components/ui/dialog";
import { Button } from "@/view/components/ui/button";
import {
  Check,
  FileText,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Trash2,
} from "lucide-react";
import useUploadFilesModal from "./useUploadFilesModal";
import { StepProgress } from "../components/StepProgress";
import { cn } from "@/app/utils/cn";

interface UploadFilesModalProps {
  onContinue: () => void;
  onBack: () => void;
}

export default function UploadFilesModal({
  onContinue,
}: UploadFilesModalProps) {
  const {
    slots,
    files,
    handleAddFile,
    handleRemoveFile,
    submitModal,
    isSubmitting,
    steps,
    isLoadingFiles,
    truncateFileName,
    isButtonDisabled,
    refreshFiles,
    isRefreshingFiles,
  } = useUploadFilesModal({ onContinue });

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const triggerFileInput = (id: string) => {
    fileInputRefs.current[id]?.click();
  };

  return (
    <DialogContent
      className="flex max-h-[90dvh] flex-col sm:max-w-3xl md:min-h-[500px]"
      closeOnClickOutside={!isSubmitting}
    >
      <DialogHeader>
        <div className="flex flex-col items-start gap-3 pr-6 md:flex-row md:justify-between md:gap-4">
          <div>
            <DialogTitle className="text-xl font-semibold text-black01">
              Enviar documento
            </DialogTitle>
            <p className="text-sm text-gray-500">
              Envie imagens ou arquivos PDF (máximo 4MB cada). O RG deve ser
              enviado em duas imagens (frente e verso).
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={refreshFiles}
            disabled={isSubmitting || isLoadingFiles || isRefreshingFiles}
            className="shrink-0 gap-2 rounded-full"
          >
            <RefreshCw
              className={cn("h-4 w-4", isRefreshingFiles && "animate-spin")}
            />
            Renovar links
          </Button>
        </div>
      </DialogHeader>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1 py-4">
        {isLoadingFiles ? (
          <div className="flex flex-1 items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          slots.map((slot) => {
            const slotFiles = files[slot.id] ?? [];
            const hasFiles = slotFiles.length > 0;
            const canAdd = slot.allowMultiple || !hasFiles;
            const isSingleFile = slotFiles.length === 1;

            const removeFile = (e: React.MouseEvent, index: number) => {
              e.stopPropagation();
              handleRemoveFile(slot.id, index);
            };

            const renderFiles = (showRemove: boolean) =>
              slotFiles.map((f, index) => (
                <div key={f.id} className="flex min-w-0 items-center gap-1">
                  <div className="flex min-w-0 items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    <FileText className="h-3 w-3 shrink-0" />
                    <span className="max-w-[150px] truncate">
                      {truncateFileName(f.file.name)}
                    </span>
                    <span className="shrink-0 text-gray-400">
                      {(f.file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  {showRemove && (
                    <button
                      type="button"
                      onClick={(e) => removeFile(e, index)}
                      className="text-gray-400 transition-colors hover:text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ));

            const preview =
              hasFiles && slotFiles[0].preview ? (
                <img
                  src={slotFiles[0].preview}
                  className="h-12 w-12 shrink-0 rounded object-cover"
                  alt="Preview"
                />
              ) : (
                <ImageIcon className="h-5 w-5 shrink-0 text-gray-400" />
              );

            return (
              <div
                key={slot.id}
                className={cn(
                  "rounded-xl border bg-white transition-all",
                  canAdd && "cursor-pointer hover:border-gray-300",
                  hasFiles && "border-green-500 ring-1 ring-green-500",
                )}
                onClick={() => canAdd && triggerFileInput(slot.id)}
              >
                <div className="group flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={cn(
                        "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border",
                        hasFiles
                          ? "border-green-100 bg-green-50 text-green-600"
                          : "border-gray-100 bg-gray-50 text-black01",
                      )}
                    >
                      {hasFiles ? (
                        <Check className="h-6 w-6" />
                      ) : (
                        <FileText className="h-6 w-6" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-black01">
                        {slot.label}
                      </h4>
                      {!hasFiles && (
                        <p className="text-sm text-gray-500">
                          Clique para enviar
                        </p>
                      )}

                      {hasFiles && (
                        <div className="mt-2 hidden flex-wrap gap-2 md:flex">
                          {renderFiles(true)}
                        </div>
                      )}
                    </div>

                    {isSingleFile && (
                      <button
                        type="button"
                        onClick={(e) => removeFile(e, 0)}
                        className="text-gray-400 transition-colors hover:text-red-500 md:hidden"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    {!hasFiles && <div className="md:hidden">{preview}</div>}
                  </div>

                  {hasFiles && (
                    <div className="flex items-center justify-between gap-3 md:hidden">
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {renderFiles(!isSingleFile)}
                      </div>
                      {preview}
                    </div>
                  )}

                  <div className="hidden md:block">{preview}</div>
                </div>

                <input
                  type="file"
                  ref={(el) => (fileInputRefs.current[slot.id] = el)}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleAddFile(slot, file);
                    e.target.value = "";
                  }}
                  accept="image/*,application/pdf"
                />
              </div>
            );
          })
        )}
      </div>

      {isSubmitting && steps.length > 0 && (
        <StepProgress
          steps={steps}
          title="Enviando documentos"
          className="mt-4"
        />
      )}

      <div className="mt-4 flex shrink-0 justify-end">
        <Button
          onClick={submitModal}
          disabled={isButtonDisabled}
          className="w-[160px] rounded-full bg-primary font-semibold text-white"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Avançar"
          )}
        </Button>
      </div>
    </DialogContent>
  );
}
